import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import {
  ArrowLeft,
  TrendingUp,
  Users,
  Target,
  DollarSign,
  Download,
  Mail,
  Building2,
  Calendar,
  CheckCircle,
  Globe,
  Smartphone,
  Trophy,
  ChevronRight,
  Loader2,
  Shield,
  Briefcase,
  BarChart3
} from 'lucide-react';
import { Button } from './ui/button';

const API = process.env.REACT_APP_BACKEND_URL ? `${process.env.REACT_APP_BACKEND_URL}/api` : '/api';

const InvestorPage = () => {
  const [showContactForm, setShowContactForm] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    company: '',
    role: '',
    investment_range: '',
    message: ''
  });

  // Set page title for SEO
  useEffect(() => {
    document.title = 'Par Seeker Investors | The Green Book - Golf Operating System';
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error('Please fill in required fields');
      return;
    }
    
    try {
      setSubmitting(true);
      await axios.post(`${API}/investor-inquiry`, form);
      setSubmitted(true);
      toast.success('Thank you for your interest. We will be in touch.');
    } catch (error) {
      toast.error('Failed to submit. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#0a0f0a]">
      {/* Navigation */}
      <nav className="border-b border-gray-800 bg-[#0a0f0a]/95 backdrop-blur sticky top-0 z-50">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-white hover:text-emerald-400 transition">
            <ArrowLeft className="w-5 h-5" />
            <span className="font-medium">Back to Par Seeker</span>
          </Link>
          <Button 
            onClick={() => setShowContactForm(true)}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            Contact Us
          </Button>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-b from-emerald-900/20 to-transparent" />
        <div className="max-w-4xl mx-auto text-center relative z-10">
          <div className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-full px-4 py-2 mb-6">
            <span className="text-emerald-400 text-sm font-medium">Founder Edition • Preview</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold text-white mb-6 leading-tight">
            The Green Book™
          </h1>
          <p className="text-xl md:text-2xl text-gray-300 mb-4">
            Par Seeker Golf Operating System™
          </p>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Building the unified ecosystem that connects golfers, courses, tournaments, leagues, and golf businesses.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/The_Green_Book_Par_Seeker_Preview_Founder_Edition.pdf"
              download
              className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-semibold px-8 py-4 rounded-xl hover:bg-gray-100 transition"
            >
              <Download className="w-5 h-5" />
              Download The Green Book
            </a>
            <Button 
              onClick={() => setShowContactForm(true)}
              variant="outline"
              className="border-emerald-500 text-emerald-400 hover:bg-emerald-500/10 px-8 py-4 h-auto"
            >
              <Mail className="w-5 h-5 mr-2" />
              Express Interest
            </Button>
          </div>
        </div>
      </section>

      {/* Company Snapshot */}
      <section className="py-16 px-6 border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Company Snapshot</h2>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {[
              { label: 'Legal Entity', value: 'DeSano Group Corporation', icon: Building2 },
              { label: 'Product', value: 'Par Seeker™', icon: Smartphone },
              { label: 'Stage', value: 'Pilot Phase', icon: Target },
              { label: 'Platform', value: 'Web Application', icon: Globe }
            ].map((item, idx) => (
              <div key={idx} className="bg-gray-900/50 border border-gray-800 rounded-xl p-5 text-center">
                <item.icon className="w-8 h-8 text-emerald-500 mx-auto mb-3" />
                <p className="text-gray-400 text-sm mb-1">{item.label}</p>
                <p className="text-white font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Vision Section */}
      <section className="py-16 px-6 bg-gradient-to-b from-emerald-900/10 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-6">Our Vision</h2>
          <p className="text-xl text-gray-300 leading-relaxed">
            To create a comprehensive Golf Operating System™ that seamlessly integrates 
            golfers, courses, tournaments, leagues, and golf businesses into one unified ecosystem — 
            making meaningful competition accessible to every golfer.
          </p>
        </div>
      </section>

      {/* Revenue Model */}
      <section className="py-16 px-6 border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Revenue Model</h2>
          <div className="grid md:grid-cols-4 gap-6">
            {[
              { title: 'Subscriptions', value: '$6.99/mo', desc: 'Premium memberships', icon: Users },
              { title: 'In-App Purchases', value: 'Golf Coins', desc: 'Virtual currency', icon: DollarSign },
              { title: 'Tournament Fees', value: 'Cash Games', desc: 'Entry fees & payouts', icon: Trophy },
              { title: 'Course Subscriptions', value: 'B2B', desc: 'Course partnerships', icon: Building2 }
            ].map((item, idx) => (
              <div key={idx} className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
                <item.icon className="w-10 h-10 text-emerald-500 mb-4" />
                <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                <p className="text-emerald-400 font-bold text-lg mb-1">{item.value}</p>
                <p className="text-gray-500 text-sm">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Founder's Statement */}
      <section className="py-16 px-6 border-t border-gray-800">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Founder&apos;s Statement</h2>
          <div className="bg-gray-900/50 border border-gray-800 rounded-2xl p-8">
            <blockquote className="text-gray-300 text-lg leading-relaxed mb-6">
              &quot;I grew up on a horse ranch in the San Bernardino mountains. From the age of seven, 
              my father put a golf club in my hands. Those early mornings on the course taught me 
              about discipline, focus, and the beauty of competition.
            </blockquote>
            <blockquote className="text-gray-300 text-lg leading-relaxed mb-6">
              Years later, as a product engineer, I watched my son try out for his high school 
              golf team. Only 10 spots for 45 kids. That&apos;s when I realized — golfers deserve 
              more opportunities to compete, connect, and grow.
            </blockquote>
            <blockquote className="text-gray-300 text-lg leading-relaxed mb-6">
              What started as a handicap calculator evolved into something bigger: 
              the Golf Operating System™. A platform where every golfer can find their game.&quot;
            </blockquote>
            <div className="flex items-center gap-4 pt-4 border-t border-gray-700">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-full flex items-center justify-center">
                <span className="text-emerald-400 font-bold text-lg">JD</span>
              </div>
              <div>
                <p className="text-white font-semibold">Founder & CEO</p>
                <p className="text-gray-500">DeSano Group Corporation</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Guiding Principles */}
      <section className="py-16 px-6 border-t border-gray-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-2xl font-bold text-white mb-8 text-center">Guiding Principles</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { title: 'Systematic Development', desc: 'Building methodically with clear purpose and direction' },
              { title: 'Respect for the Sport', desc: 'Honoring golf traditions while innovating for the future' },
              { title: 'Community Focus', desc: 'Putting golfers and their experiences first' },
              { title: 'Mission Protection', desc: 'Staying true to our core vision and values' },
              { title: 'Integrated Experience', desc: 'Every feature strengthens the Operating System' },
              { title: 'Accessible Competition', desc: 'Creating opportunities for golfers at every level' }
            ].map((item, idx) => (
              <div key={idx} className="flex gap-4">
                <CheckCircle className="w-6 h-6 text-emerald-500 flex-shrink-0 mt-1" />
                <div>
                  <h3 className="text-white font-semibold mb-1">{item.title}</h3>
                  <p className="text-gray-400 text-sm">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Investment Overview */}
      <section className="py-16 px-6 border-t border-gray-800 bg-gradient-to-b from-emerald-900/10 to-transparent">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-2xl font-bold text-white mb-8">Investment Overview</h2>
          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-gray-900/50 border border-gray-800 rounded-xl p-6">
              <p className="text-gray-400 text-sm mb-2">Founder Investment</p>
              <p className="text-3xl font-bold text-white">~$250,000</p>
              <p className="text-emerald-400 text-sm mt-2">Capital deployed to date</p>
            </div>
            <div className="bg-gray-900/50 border border-emerald-500/30 rounded-xl p-6">
              <p className="text-gray-400 text-sm mb-2">Series Seed Target</p>
              <p className="text-3xl font-bold text-emerald-400">Up to $5M</p>
              <p className="text-gray-400 text-sm mt-2">Equity financing round</p>
            </div>
          </div>
          <p className="text-gray-400 text-sm max-w-2xl mx-auto">
            Note: The Series Seed target represents the funding round size, not company valuation. 
            Valuation will be determined based on market conditions, traction, and investor discussions.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6 border-t border-gray-800">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Interested in Learning More?</h2>
          <p className="text-gray-400 mb-8">
            Download The Green Book for the complete founder&apos;s vision, or reach out to discuss 
            partnership and investment opportunities.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a 
              href="/The_Green_Book_Par_Seeker_Preview_Founder_Edition.pdf"
              download
              className="inline-flex items-center justify-center gap-2 bg-white text-gray-900 font-semibold px-8 py-4 rounded-xl hover:bg-gray-100 transition"
            >
              <Download className="w-5 h-5" />
              Download The Green Book
            </a>
            <Button 
              onClick={() => setShowContactForm(true)}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-8 py-4 h-auto"
            >
              Contact Investor Relations
            </Button>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8 px-6">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-2">
            <span className="text-emerald-500 font-bold text-xl">Par Seeker</span>
            <span className="text-gray-600">|</span>
            <span className="text-gray-500 text-sm">DeSano Group Corporation</span>
          </div>
          <p className="text-gray-500 text-sm">
            © {new Date().getFullYear()} DeSano Group Corporation. All rights reserved.
          </p>
        </div>
      </footer>

      {/* Contact Form Modal */}
      {showContactForm && (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
          <div className="bg-gray-900 rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto">
            <div className="p-6">
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-xl font-bold text-white">Investor Inquiry</h2>
                <button 
                  onClick={() => setShowContactForm(false)}
                  className="text-gray-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              {submitted ? (
                <div className="text-center py-8">
                  <CheckCircle className="w-16 h-16 text-emerald-500 mx-auto mb-4" />
                  <h3 className="text-xl font-bold text-white mb-2">Thank You</h3>
                  <p className="text-gray-400">
                    We&apos;ve received your inquiry and will be in touch shortly.
                  </p>
                  <Button 
                    onClick={() => setShowContactForm(false)}
                    className="mt-6 bg-emerald-600 hover:bg-emerald-700"
                  >
                    Close
                  </Button>
                </div>
              ) : (
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Full Name *</label>
                    <input
                      type="text"
                      value={form.name}
                      onChange={(e) => setForm({...form, name: e.target.value})}
                      required
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white"
                      placeholder="John Smith"
                    />
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Email *</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({...form, email: e.target.value})}
                      required
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white"
                      placeholder="john@example.com"
                    />
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Company/Fund</label>
                      <input
                        type="text"
                        value={form.company}
                        onChange={(e) => setForm({...form, company: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white"
                        placeholder="Acme Ventures"
                      />
                    </div>
                    <div>
                      <label className="block text-gray-400 text-sm mb-2">Role</label>
                      <input
                        type="text"
                        value={form.role}
                        onChange={(e) => setForm({...form, role: e.target.value})}
                        className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white"
                        placeholder="Partner"
                      />
                    </div>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Investment Range</label>
                    <select
                      value={form.investment_range}
                      onChange={(e) => setForm({...form, investment_range: e.target.value})}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white"
                    >
                      <option value="">Select range</option>
                      <option value="<100k">Under $100K</option>
                      <option value="100k-250k">$100K - $250K</option>
                      <option value="250k-500k">$250K - $500K</option>
                      <option value="500k-1m">$500K - $1M</option>
                      <option value=">1m">$1M+</option>
                      <option value="strategic">Strategic Partner</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-gray-400 text-sm mb-2">Message</label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({...form, message: e.target.value})}
                      rows={4}
                      className="w-full bg-gray-800 border border-gray-700 rounded-lg px-4 py-3 text-white resize-none"
                      placeholder="Tell us about your interest in Par Seeker..."
                    />
                  </div>
                  <Button 
                    type="submit"
                    disabled={submitting}
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white py-4"
                  >
                    {submitting ? (
                      <Loader2 className="w-5 h-5 animate-spin" />
                    ) : (
                      'Submit Inquiry'
                    )}
                  </Button>
                  <p className="text-gray-500 text-xs text-center">
                    Your information is kept confidential and will only be used for investor communications.
                  </p>
                </form>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default InvestorPage;
